/**
 * SoftNavigationBridge
 *
 * Bridges non-React navigation requests (`ta-request-navigation` CustomEvents
 * dispatched via requestSoftNavigation()) to the Next.js app router. Mounted
 * once at the root layout so auth/session teardown code can navigate without
 * a full WebView reload on persistent platforms (Capacitor/Tauri/PWA).
 *
 * The handler calls event.preventDefault() so dispatchers can tell the
 * request was consumed — when no bridge is mounted, callers fall back to
 * window.location.assign().
 */

'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  TA_SOFT_NAVIGATION_EVENT,
  type SoftNavigationRequest,
} from '@/lib/platformUtils';

export default function SoftNavigationBridge() {
  const router = useRouter();

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<SoftNavigationRequest>).detail;
      if (!detail?.url || typeof detail.url !== 'string') return;

      event.preventDefault();
      if (detail.replace) {
        router.replace(detail.url);
      } else {
        router.push(detail.url);
      }
    };

    window.addEventListener(TA_SOFT_NAVIGATION_EVENT, handler);
    return () => window.removeEventListener(TA_SOFT_NAVIGATION_EVENT, handler);
  }, [router]);

  return null;
}
